/**********************************************************************
        Modules to comment
**********************************************************************/

import {getTimeAgo} from "../helpers/time-converter.js"
import { handleErrorWithAlert } from "../../utils/fetch-json.js"
import { el, setAttr } from "redom"
import { openUserPageOnClick } from "../pages/userpage.js"
import { escapeHtml } from "../../utils/common.js"
import { createMenuButton, createReportUserButton, SubmitModal } from "../components/build-modal.js"
import type { User, MaybeUser, UserDetails, CommentElement, Comment, CommentWithUser, ModalButton, CommentModerationAction } from "#/types"
import { maybeMeIsMe } from "../../utils/authUser.js"
import { CommentService } from "../services/comment-service.js"

const maxCommentCharacter: number = 140
const hiddenCommentMessage = "the comment is hidden"

const applyModerationAction = (commentElement: CommentElement, action: CommentModerationAction, originalComment: string) => {
    if (action === "block") {
        commentElement.className = "badComment"
    } else if (action === "hide") {
        commentElement.comment = hiddenCommentMessage
        commentElement.className = "badComment"
        commentElement.revealButton = el("button", { className: "btn btn-small btn-default clickable", style: "margin: 0 0 0 5px; padding: 0; vertical-align:baseline;", onclick: () => revealComment(commentElement.createdAt, originalComment) }, "reveal")
    }
}

const showDeletedContent = (createdAt: number) => {
    const deleteContent = document.getElementById(createdAt + "Content")
    const menuButton = document.getElementById(`commentMenuButton-${createdAt}`)
    if (deleteContent) {
        deleteContent.classList.add("deletedComment")
        deleteContent.classList.remove("badComment")
        deleteContent.innerHTML = "comment has been deleted"
    }
    if (menuButton) menuButton.style.display = "none"
}

const deleteCommentModal = new SubmitModal("deleteCommentModal", "DELETE COMMENT",
    el("p", { style: "text-align: center; text-overflow: ellipsis; overflow: hidden; font-size: 25; margin: 0px;" }, "Are you sure you want to delete this comment?"),
    "DELETE")

export const commentDeletionModalElement = deleteCommentModal.el

const deleteComment = (recordId: string, createdAt: number) => {
    deleteCommentModal.disableSubmitButton()
    CommentService.deleteComment(recordId, createdAt)
        .then(() => showDeletedContent(createdAt))
        .catch(handleErrorWithAlert)
        .finally(() => deleteCommentModal.enableSubmitButton())
}
// TODO: to combine assembleCommentTable() with buildCommentContent()
export const assembleCommentTable = (recordId: string, commentElement: CommentElement, isAllowedToDelete: boolean, myId: string, isDeleted: boolean = false):HTMLLIElement => {
    const user: UserDetails = commentElement.user
    const buttons:ModalButton[] = []
    if (isAllowedToDelete) {
        const deleteButton = { 
            icon: el("i.far fa-trash-alt fa-fw"), 
            text: "Delete", 
            action: () => deleteCommentModal.open(() => deleteComment(recordId, commentElement.createdAt))
        }
        buttons.push(deleteButton)
    }
    if (!isDeleted && myId != user.id) buttons.push(createReportUserButton(user, myId, commentElement.comment, commentElement.createdAt, window.location.href))
    const modalDivs = el("div")

    const menuButton:HTMLButtonElement = createMenuButton("more_contents_on_comments" + commentElement.createdAt, buttons, modalDivs) as HTMLButtonElement
    menuButton.id = `commentMenuButton-${commentElement.createdAt}`
    menuButton.style.fontSize = "15px"

    return el("li.comment-table d-flex",
        el("a.flex-shrink-0", { style: "padding-top: 3px" },
            el("img.rounded-circle clickable", { width: "40", src: user.imgUrl, onerror: (e: Event) => { (e.target as HTMLImageElement).src = "/public/img/icon200x200.png" }, onclick: () => openUserPageOnClick(user.id) })
        ),
        el("div.comment-body flex-grow-1",
            el("div", { className: "float-end" }, menuButton),
            el("h6", el(".clickable", { onclick: () => openUserPageOnClick(user.id) }, user.name ) ),
            el("span#" + commentElement.createdAt + "Content.comment " + (commentElement.className ?? ""), { style: "padding-left: 5px" }, [ commentElement.comment, commentElement.revealButton ] ),
            el("br"),
            el("span.text-muted", el("small", getTimeAgo(commentElement.createdAt)))
        ),
        modalDivs
    )
}

export const buildCommentContent = (commentWithUser: CommentWithUser, me: MaybeUser, recordId: string): HTMLLIElement => {
    const comment: Comment = commentWithUser.comment
    const isAllowedToDelete: boolean = maybeMeIsMe(me) && !comment.ifDeleted && (me.get.id == comment.userId || me.get.id == comment.recordUploader)
    const commentElement: CommentElement = { comment: comment.message, createdAt: comment.createdAt, user: commentWithUser.user }
    if (comment.ifDeleted) {
        commentElement.className = "deletedComment"
    } else applyModerationAction(commentElement, comment.moderationAction, comment.message)
    return assembleCommentTable(recordId, commentElement, isAllowedToDelete, (me as User)?.get?.id, comment.ifDeleted)
}

const revealComment = (createdAt: number, comment: string) => {
    const commentSpan = document.getElementById(createdAt + "Content")
    if (commentSpan) {
        commentSpan.innerHTML = escapeHtml(comment)
        commentSpan.classList.remove("badComment")
    }
}

const compressNewlines = (comment: string): string => {
    let result: string =  ""
    for(let i = 0; i < comment.length; i += 1){
        if(i > 0 && comment[i] == '\n' && (comment[i - 1] == '\n' || comment[i - 1] == ' ' || comment[i - 1] == '\t'))
            continue
        result += comment[i]
    }
    return result
}

export const postComment = (uploaderId: string, recordId: string, me: MaybeUser, unrefinedComment: string) => {
    const comment: string = compressNewlines(unrefinedComment.trim())
    return CommentService.postComment(uploaderId, recordId, comment).then(response => {
        const timestamp: number = response.createdAt
        const comments = document.getElementsByClassName("comment-list")[0]
        const commentElement: CommentElement = { user: (me as User)?.get, comment, createdAt: timestamp }
        applyModerationAction(commentElement, response.moderationAction, comment)
        comments.insertBefore(assembleCommentTable(recordId, commentElement, true, (me as User)?.get?.id), comments.childNodes[0])
        clearCommentArea()
    }).catch(handleErrorWithAlert)
}

const disableCommentButton = (disabled: boolean) => {
    const commentButton = document.getElementById("commentButton")
    if (commentButton) setAttr(commentButton, { "disabled": disabled })
}

const clearCommentArea = () => {
    const commentText = document.getElementById("commentText") as HTMLTextAreaElement | null;
    const charCountEl = document.getElementById("commentCharactersLeft");

    if (commentText) {
        commentText.removeEventListener('input', autoExpandHandler);
        commentText.removeEventListener('focus', oneTimeFocusHandler);
        commentText.value = "";
        commentText.setAttribute("rows", "1");
    }

    if (charCountEl) {
        charCountEl.textContent = String(maxCommentCharacter);
    }
    disableCommentButton(true)
}

const autoExpandHandler = (e: Event) => {
    const el = e.target as HTMLTextAreaElement;
    const minRows = parseInt(el.getAttribute("data-min-rows") || "1", 10);
    const baseScrollHeight = (el as any).baseScrollHeight || el.scrollHeight;

    el.rows = minRows;
    const additionalRows = Math.ceil((el.scrollHeight - baseScrollHeight) / 24);
    el.rows = minRows + additionalRows;
};

const oneTimeFocusHandler = (e: FocusEvent) => {
    const el = e.target as HTMLTextAreaElement;
    const savedValue = el.value;
    el.value = "";
    (el as any).baseScrollHeight = el.scrollHeight;
    el.value = savedValue;

    el.removeEventListener("focus", oneTimeFocusHandler);
};

export const commentInit = () => {
    clearCommentArea()
    const commentText = document.getElementById("commentText") as HTMLTextAreaElement | null;
    const charCountEl = document.getElementById("commentCharactersLeft");

    if (!commentText) return;

    commentText.addEventListener("keyup", () => {
        const currentText = commentText.value;
        const hasNoComment: boolean = currentText.trim() === ""
        disableCommentButton(hasNoComment);
        if (charCountEl) {
            charCountEl.textContent = String(maxCommentCharacter - currentText.length);
        }
    });
    commentText.addEventListener("focus", oneTimeFocusHandler);
    commentText.addEventListener("input", autoExpandHandler);
}